import { Button } from "../../components";
import { t } from "../../i18n/t";

/** Above this many characters an option's text no longer fits a table cell's select, so its full wording is
 * also shown, wrapped, under the select (the closed select itself truncates with an ellipsis). */
export const LONG_OPTION_CHARS = 40;

/**
 * xmg-webux (first staging trial, defect 1): the option picker + Resolve button every review table renders
 * (OtherReviewItems, VatQueue, DimensionLoss). A producer's option can be a whole sentence ("acknowledge:
 * proceed without Xero's system-generated postings after the conversion date; ..."); a bare `<select>` in a
 * `nowrap` cell sized itself to that sentence, pushed the table far past the card, and the Resolve button
 * ended up off-screen with the select looking empty. Here the select is bounded by its cell (width 100%,
 * ellipsis), the chosen option's full text wraps underneath it when it is long, and the Resolve button sits
 * BELOW the select so it is always on screen.
 */
export function ResolveControl({
  selectId,
  code,
  options,
  picked,
  labelOf = (option) => option,
  disabled,
  resolveDisabled,
  onPick,
  onResolve,
}: {
  selectId: string;
  /** The row's business key, used in the select's label and the button's name. */
  code: string;
  options: readonly string[];
  picked: string;
  labelOf?: (option: string) => string;
  /** Disables the select (read-only step, or nothing to choose). */
  disabled: boolean;
  /** Disables the Resolve button. */
  resolveDisabled: boolean;
  onPick: (option: string) => void;
  onResolve: () => void;
}): JSX.Element {
  const pickedLabel = picked === "" ? "" : labelOf(picked);
  return (
    <div className="optionpicker" data-testid={`optionpicker-${selectId}`}>
      <label className="visually-hidden" htmlFor={selectId}>
        {t("review.choose_option", { code })}
      </label>
      <select
        id={selectId}
        className="cellselect optionpicker__select"
        value={picked}
        title={pickedLabel === "" ? undefined : pickedLabel}
        disabled={disabled}
        onChange={(event) => onPick(event.target.value)}
      >
        {/* `label` (not text) so the closed select reads "Choose an answer" while the option's text stays empty. */}
        <option value="" label={t("review.choose_placeholder")} />
        {options.map((option) => (
          <option key={option} value={option}>
            {labelOf(option)}
          </option>
        ))}
      </select>
      {pickedLabel.length > LONG_OPTION_CHARS ? (
        <p className="optionpicker__full" data-testid={`${selectId}-full`}>
          {pickedLabel}
        </p>
      ) : null}
      {picked === "" && options.some((option) => labelOf(option).length > LONG_OPTION_CHARS) ? (
        // A closed select truncates a sentence-long option; before anything is picked, every option is
        // readable in full here, so the operator can choose without opening the dropdown blind.
        <details className="optionpicker__all" data-testid={`${selectId}-all`}>
          <summary>{t("review.read_options", { n: options.length })}</summary>
          <ul>
            {options.map((option) => (
              <li key={option}>{labelOf(option)}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <Button
        className="button--wrap optionpicker__resolve"
        aria-label={t("review.resolve", { code })}
        disabled={resolveDisabled}
        onClick={onResolve}
      >
        {t("review.resolve_short")}
      </Button>
    </div>
  );
}
